import { useState } from 'react'
import { Button, Form, Select, InputNumber, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { approvalMatrixApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { ApprovalMatrix } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'

const MODULES = ['PR', 'PO', 'WO']

export default function ApprovalMatrixPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['approval-matrix', plantFilter],
    queryFn: () => approvalMatrixApi.list({ plant_id: plantFilter }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editId ? approvalMatrixApi.update(editId, values) : approvalMatrixApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-matrix'] })
      message.success(editId ? 'Updated' : 'Created')
      setDrawerOpen(false)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openCreate = () => {
    setEditId(null)
    form.resetFields()
    form.setFieldsValue({ level: 1, min_amount: 0 })
    setDrawerOpen(true)
  }

  const openEdit = (r: ApprovalMatrix) => {
    setEditId(r.id)
    form.setFieldsValue({ ...r, approver_role_id: r.approver_role_id })
    setDrawerOpen(true)
  }

  const columns = [
    { title: 'Module', dataIndex: 'module', key: 'module', width: 80 },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    { title: 'Level', dataIndex: 'level', key: 'level', width: 70 },
    { title: 'Min Amount', dataIndex: 'min_amount', key: 'min_amount', width: 120 },
    { title: 'Max Amount', dataIndex: 'max_amount', key: 'max_amount', width: 130,
      render: (v: string | null) => v ?? 'Unlimited' },
    { title: 'Approver Role', dataIndex: 'role_name', key: 'role_name' },
    {
      title: '', key: 'edit', width: 60,
      render: (_: unknown, r: ApprovalMatrix) => <a onClick={() => openEdit(r)}>Edit</a>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Approval Matrix"
        subtitle="Configure approval levels and amount thresholds per module"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Rule</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select
          allowClear placeholder="Filter by plant"
          options={plantOptions}
          onChange={v => setPlantFilter(v)}
          style={{ width: 200 }}
        />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title={editId ? 'Edit Approval Rule' : 'New Approval Rule'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => saveMutation.mutate(v))}
        loading={saveMutation.isPending}
        form={form}
      >
        <Form.Item name="module" label="Module" rules={[{ required: true }]}>
          <Select options={MODULES.map(m => ({ label: m, value: m }))} disabled={!!editId} />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} disabled={!!editId} />
        </Form.Item>
        <Form.Item name="level" label="Approval Level" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={1} max={5} />
        </Form.Item>
        <Form.Item name="min_amount" label="Min Amount (₹)" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="max_amount" label="Max Amount (₹) — blank = unlimited">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
